import {
  AutomationDeliveryProvider,
  automationResultModeSchema,
  automationResultSelectionModeSchema,
  AutomationTemplateIds,
  buildDefaultAutomationDeliveryForProvider,
  checkIsTeammateCoachingSettings,
  DEFAULT_AUTOMATION_DELIVERY,
  getAutomationDeliveryEditorValue,
  getAutomationManagerRecipientsEditorValue,
  getAutomationPeopleSelection,
  getAutomationPlaybookIds,
  getAutomationRecipientSelection,
  getAutomationReportDefinition,
  getAutomationResultMode,
  getAutomationResultSelectionMode,
  getAutomationSubscribersEditorValue,
  LEAD_BURNING_DAILY_TIME_OF_DAY,
  partitionAutomationPeopleSelection,
  updateAutomationDeliveryEditorValue,
  updateAutomationManagerRecipientsEditorValue,
  updateAutomationPeopleSelection,
  updateAutomationPlaybookIds,
  updateAutomationRecipientSelection,
  updateAutomationResultMode,
  updateAutomationResultSelectionMode,
  updateAutomationSubscribersEditorValue,
  type AutomationDelivery,
  type AutomationPeopleSelection,
  type AutomationRecipientSelection,
  type AutomationReportAgentSettings,
  type AutomationReportInstances,
  type AutomationResultMode,
  type AutomationResultSelectionMode,
  type AutomationSettings,
  type AutomationTemplateId,
  type TeammateCoachingAutomationSettings,
} from "@repo/app-types/entities/automation";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import {
  allScheduleWeekdays,
  scheduleWeekdaySchema,
  type RecurringSchedule,
  type ScheduleFrequency,
  type ScheduleWeekday,
} from "@repo/app-types/scheduling/recurringSchedule";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Checkbox } from "@repo/ui/Checkbox/checkbox";
import { ComboboxField } from "@repo/ui/combobox/comboboxField";
import { Size } from "@repo/ui/helpers/helpers";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { SimpleRadioGroup } from "@repo/ui/Radio/simpleRadioGroup";
import { SimpleRadioOption } from "@repo/ui/Radio/simpleRadioOption";
import {
  formatPlatformZoneAbbreviation,
  monthlyScheduleOptions,
  monthlyScheduleToOptionValue,
  RecurringScheduleFields,
  scheduleFrequencyLabels,
  weekdayLabels,
  type MonthlyScheduleOption,
} from "@repo/ui/scheduling/recurringScheduleFields";
import { MultiSelect } from "@repo/ui/Select/multiSelect";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { TimeSelect } from "@repo/ui/Select/timeSelect";
import { IconAlertTriangle } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { useEffect, useState, type FC, type ReactNode } from "react";
import { useIsFeatureFlagEnabled } from "../../helpers/featureFlags/hooks.js";
import { Can } from "../../helpers/teammateAuthorizationContext.js";
import { openOAuthSigninPopupForIntegration } from "../../helpers/urlHelpers.js";
import { useAppSelector } from "../../hooks/hook.js";
import { buildPlaybookAndVoiceAgentSections } from "../../hooks/usePlaybookAndVoiceAgentOptions.js";
import { ConfigurationField } from "./configurationField.js";
import {
  allDeliveryProviderDefinitions,
  checkIsDeliveryProvider,
  deliveryProviderDefinitions,
} from "./deliveryProviders.js";
import { GroupPicker, type GroupOption } from "./groupPicker.js";
import { ManagerScopeField } from "./managerScopeField.js";
import { PeoplePicker } from "./peoplePicker.js";
import { TeammatePicker, type TeammateOption } from "./teammatePicker.js";

/**
 * Height bounds every free-text field that shapes an agent's prompt opens at
 * and grows within. The floor matches the custom agent's "What should this
 * agent look for?" prompt and the ceiling the playbook editor's grading
 * context fields, so a long prompt stays readable without pushing the rest of
 * the form off the screen.
 */
const promptGuidanceHeightClassName = "min-h-[200px] max-h-[400px]";

export interface AutomationFieldsContext {
  playbookOptions: readonly SimpleSelectOption<PlaybookId>[];
  /**
   * Voice agents offered alongside playbooks in the report-scope picker, each
   * valued by the playbook backing the agent. Optional so single-playbook
   * forms and fixtures need not supply it.
   */
  voiceAgentOptions?: readonly SimpleSelectOption<PlaybookId>[];
  arePlaybooksLoading: boolean;
  /**
   * True while the teammate/group recipient options are still loading. Recipient
   * pickers render disabled until the options arrive, so an in-flight fetch is
   * never shown as an empty menu that reads as "no eligible teammates."
   */
  areRecipientOptionsLoading: boolean;
  /** All teammates, for controls that may pick anyone (e.g. managers). */
  teammateOptions: readonly TeammateOption[];
  /** Editors and reviewers eligible for non-custom automation notifications. */
  editorAndReviewerTeammateOptions: readonly TeammateOption[];
  /** Teammate group options for the custom agent's "Notify" groups picker. */
  groupOptions: readonly GroupOption[];
  /**
   * Teammates who currently manage a non-empty group. Decides which picked
   * people count as managers — both for the description under the people
   * picker and for how the selection is partitioned into the saved definition.
   */
  managerTeammateIds: readonly TeammateId[];
  /**
   * Playbooks already governed by a coaching automation. Used to disable them
   * in the coaching agent's playbook dropdown so at most one coaching agent
   * exists per playbook (also enforced server-side). Optional so non-coaching
   * forms and fixtures need not supply it.
   */
  coachingPlaybookIdsInUse?: readonly PlaybookId[];
  /**
   * Renders every field disabled for teammates who can view but not update
   * automations (reviewers). Optional so fixtures need not supply it.
   */
  isReadOnly?: boolean;
}

export interface AutomationSettingsFieldsProps extends AutomationFieldsContext {
  templateId: AutomationTemplateId;
  settings: AutomationSettings;
  onChange: (settings: AutomationSettings) => void;
}

/**
 * Renders the settings form for an automation. Every report template persists
 * the same canonical settings shape.
 */
export const AutomationSettingsFields: FC<AutomationSettingsFieldsProps> = (
  props,
) => {
  const { templateId, settings, onChange, ...fieldsContext } = props;
  // The one structurally distinct settings shape. Rendering it under any other
  // template id (or a report template's shape under coaching, below) is a
  // caller bug; render nothing rather than another template's form.
  if (checkIsTeammateCoachingSettings(settings)) {
    return templateId === AutomationTemplateIds.TEAMMATE_COACHING ? (
      <TeammateCoachingSettingsFields
        {...fieldsContext}
        settings={settings}
        onChange={onChange}
      />
    ) : null;
  }
  switch (templateId) {
    case AutomationTemplateIds.PLAYBOOK_SCORE_MOVEMENT:
      return (
        <PlaybookScoreMovementSettingsFields
          {...fieldsContext}
          settings={settings}
          onChange={onChange}
        />
      );
    case AutomationTemplateIds.LEAD_BURNING:
      return (
        <LeadBurningSettingsFields
          {...fieldsContext}
          settings={settings}
          onChange={onChange}
        />
      );
    case AutomationTemplateIds.CUSTOMER_PRAISE_DIGEST:
      return (
        <CustomerPraiseDigestSettingsFields
          {...fieldsContext}
          settings={settings}
          onChange={onChange}
        />
      );
    case AutomationTemplateIds.PERFECT_CALLS:
      return (
        <PerfectCallsSettingsFields
          {...fieldsContext}
          settings={settings}
          onChange={onChange}
        />
      );
    case AutomationTemplateIds.CUSTOM:
      return (
        <CustomSettingsFields
          {...fieldsContext}
          settings={settings}
          onChange={onChange}
        />
      );
    default:
      return null;
  }
};

interface TeammateCoachingSettingsFieldsProps extends AutomationFieldsContext {
  settings: TeammateCoachingAutomationSettings;
  onChange: (settings: TeammateCoachingAutomationSettings) => void;
}

interface ChannelOption {
  id: string;
  name: string;
}

interface NotificationDeliveryFieldsProps {
  /** May be absent on rollout-era revisions already loaded by the client. */
  delivery: AutomationDelivery | undefined;
  onChangeDelivery: (delivery: AutomationDelivery) => void;
  /**
   * Allow templates to disable channel destination for agents outputting private information e.g. lead burning alerts about individual reps
   */
  allowChannelDestination?: boolean;
  /** Renders the delivery controls disabled (read-only view). */
  isReadOnly?: boolean;
  /** The report definition contains routing this form cannot safely rewrite. */
  isRoutingReadOnly?: boolean;
  /**
   * Rendered above the delivery controls — the custom template's "Result type"
   * choice, which frames what "each result" below refers to.
   */
  resultTypeField?: ReactNode;
  /**
   * Every result reaches one person privately, so there is no shared
   * destination to choose: providers show their direct-message label and the
   * channel/DM chooser is skipped. Set when each person gets their own result.
   */
  isPrivateOnly?: boolean;
  /** Recipient controls, shown under Email and under "Direct messages". */
  children: ReactNode;
}

// Stand-in selector for providers that need no integration (email), so the
// integration lookup stays a single unconditional useAppSelector call.
const selectNoIntegration = (): { id: string } | null => null;

/**
 * "Delivery channel" section: where an agent sends each result. Everything
 * provider-specific (labels, icons, integration lookup, channel listing) comes
 * from `deliveryProviderDefinitions`; this component only renders capabilities.
 * Providers without destination controls (email) show the recipient controls
 * directly; the rest get a channel/private radio, a channel picker fed by the
 * provider's channel listing, and a Connect CTA when its integration is
 * missing.
 */
const NotificationDeliveryFields: FC<NotificationDeliveryFieldsProps> = (
  props,
) => {
  const {
    delivery,
    onChangeDelivery,
    allowChannelDestination = true,
    isReadOnly,
    isRoutingReadOnly,
    resultTypeField,
    isPrivateOnly = false,
    children,
  } = props;
  const effectiveDelivery = delivery ?? DEFAULT_AUTOMATION_DELIVERY;

  const isTeamsEnabled = useIsFeatureFlagEnabled(FeatureFlagKey.MicrosoftTeams);
  const visibleProviderDefinitions = allDeliveryProviderDefinitions.filter(
    (definition) =>
      definition.id !== AutomationDeliveryProvider.TEAMS || isTeamsEnabled,
  );

  const providerDefinition =
    deliveryProviderDefinitions[effectiveDelivery.provider];
  const connection = providerDefinition.connection;
  const destinationConfig = providerDefinition.destinationConfig;

  const integration = useAppSelector(
    connection ? connection.select : selectNoIntegration,
  );

  const [channelOptions, setChannelOptions] = useState<ChannelOption[]>([]);

  useEffect(() => {
    if (!destinationConfig || !integration) {
      setChannelOptions([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const channels = await destinationConfig.channel.list(integration.id);
        if (cancelled) return;
        setChannelOptions(
          channels.map((channel) => ({
            id: channel.id,
            name: channel.name,
          })),
        );
      } catch {
        if (!cancelled) {
          setChannelOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [destinationConfig, integration]);

  const chatDelivery =
    effectiveDelivery.provider === AutomationDeliveryProvider.EMAIL
      ? null
      : effectiveDelivery;
  const destination = chatDelivery?.destination ?? {
    kind: "direct_message" as const,
  };
  const destinationType =
    allowChannelDestination && destination.kind === "channel"
      ? "channel"
      : "direct_message";
  const firstChannel =
    destination.kind === "channel" ? (destination.channels[0] ?? null) : null;
  const selectedChannel: ChannelOption | null = firstChannel
    ? {
        id: firstChannel.id,
        name: firstChannel.name ?? firstChannel.id,
      }
    : null;

  // The saved channel may not be among the fetched options (a private channel,
  // or options still loading), so keep it in the list or its name would vanish.
  const channelItems =
    selectedChannel &&
    !channelOptions.some((option) => option.id === selectedChannel.id)
      ? [selectedChannel, ...channelOptions]
      : channelOptions;

  const destinationFields =
    !destinationConfig ? null : allowChannelDestination ? (
      <SimpleRadioGroup
        value={destinationType}
        onChange={(value) => {
          if (!chatDelivery) {
            return;
          }
          if (value === "channel") {
            onChangeDelivery({
              ...chatDelivery,
              destination: { kind: "channel", channels: [] },
            });
          } else if (value === "direct_message") {
            onChangeDelivery({
              ...chatDelivery,
              destination: { kind: "direct_message" },
            });
          }
        }}
      >
        <div className="flex flex-col gap-3">
          <SimpleRadioOption value="channel" isDisabled={isReadOnly}>
            {destinationConfig.channel.label}
          </SimpleRadioOption>
          {destinationType === "channel" ? (
            <div className="ml-6 flex flex-col gap-2">
              <ComboboxField<ChannelOption>
                aria-label={destinationConfig.channel.label}
                items={channelItems}
                getLabel={(option) => option.name}
                value={selectedChannel?.id ?? null}
                onChange={(key) => {
                  // Clearing the text reports no selection. There was never a
                  // way to unset the channel, and this form autosaves, so keep
                  // the current one rather than saving an empty destination.
                  if (!chatDelivery || key === null) {
                    return;
                  }
                  const channel = channelItems.find(
                    (option) => option.id === key,
                  );
                  if (!channel) {
                    return;
                  }
                  onChangeDelivery({
                    ...chatDelivery,
                    destination: {
                      kind: "channel",
                      channels: [{ id: channel.id, name: channel.name }],
                    },
                  });
                }}
                placeholder={destinationConfig.channel.pickerPlaceholder}
                isDisabled={isReadOnly || isRoutingReadOnly}
              />
              {selectedChannel ? (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-900">
                  <IconAlertTriangle
                    aria-hidden
                    className="mt-0.5 size-4 shrink-0"
                  />
                  <p>
                    Everyone in {selectedChannel.name} can see this agent&apos;s
                    output and its link back to Alpharun.
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-3">
          <SimpleRadioOption value="direct_message" isDisabled={isReadOnly}>
            {destinationConfig.directMessage.label}
          </SimpleRadioOption>
          {destinationType === "direct_message" ? (
            <div className="ml-6 flex flex-col gap-5">{children}</div>
          ) : null}
        </div>
      </SimpleRadioGroup>
    ) : (
      <div className="flex flex-col gap-5">
        <p className="text-xs leading-5 text-muted-foreground">
          {destinationConfig.directMessage.explanation}
        </p>
        {children}
      </div>
    );

  return (
    <div className="flex flex-col gap-5 pt-5">
      <div>
        <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
        <p className="mt-1 max-w-xl text-xs leading-5 text-muted-foreground">
          Choose where this agent sends each result. Results are always saved in
          Alpharun.
        </p>
      </div>

      {resultTypeField}

      <ConfigurationField label="Delivery channel">
        <div className="max-w-xl">
          <SimpleRadioGroup
            value={effectiveDelivery.provider}
            onChange={(value) => {
              if (
                !checkIsDeliveryProvider(value) ||
                value === effectiveDelivery.provider
              ) {
                return;
              }
              onChangeDelivery(
                buildDefaultAutomationDeliveryForProvider(value),
              );
            }}
          >
            {visibleProviderDefinitions.map((provider) => (
              <SimpleRadioOption
                key={provider.id}
                value={provider.id}
                isDisabled={isReadOnly || isRoutingReadOnly}
              >
                <span className="flex items-center gap-2">
                  {provider.renderIcon()}
                  <span>
                    {isPrivateOnly
                      ? provider.directMessageLabel
                      : provider.label}
                  </span>
                </span>
              </SimpleRadioOption>
            ))}
          </SimpleRadioGroup>

          <div className="mt-5">
            {isRoutingReadOnly ? (
              <div className="flex flex-col gap-5">
                <p className="text-xs leading-5 text-muted-foreground">
                  Result destinations are configured in the report definition.
                </p>
                {children}
              </div>
            ) : !destinationConfig ? (
              <div className="flex flex-col gap-5">{children}</div>
            ) : connection && !integration ? (
              <Can
                I={PermissionActions.CREATE}
                a={PermissionSubjects.INTEGRATIONS}
                passThrough={true}
              >
                {(allowed) => (
                  <Button
                    size={Size.SMALL}
                    variant={ButtonVariant.Secondary}
                    isDisabled={!allowed}
                    onClick={() => {
                      void openOAuthSigninPopupForIntegration(
                        connection.oauthProvider,
                      );
                    }}
                  >
                    Connect {providerDefinition.label}
                  </Button>
                )}
              </Can>
            ) : isPrivateOnly ? (
              // Each result reaches one person, so recipients are the whole
              // destination — there is nothing to choose between.
              <div className="flex flex-col gap-5">{children}</div>
            ) : (
              <>
                <div className="text-xs font-medium text-muted-foreground">
                  {providerDefinition.label} destination
                </div>
                <div className="mt-3">{destinationFields}</div>
              </>
            )}
          </div>
        </div>
      </ConfigurationField>
    </div>
  );
};

const TeammateCoachingSettingsFields: FC<
  TeammateCoachingSettingsFieldsProps
> = (props) => {
  const { settings, onChange, ...fieldsContext } = props;
  return (
    <>
      <PlaybookConfigurationField
        {...fieldsContext}
        playbookId={settings.playbook_id}
        // Disable playbooks taken by another coaching agent, but keep this
        // agent's own playbook selectable.
        disabledPlaybookIds={(
          fieldsContext.coachingPlaybookIdsInUse ?? []
        ).filter((playbookId) => playbookId !== settings.playbook_id)}
        onChange={(playbookId) => {
          onChange({ ...settings, playbook_id: playbookId });
        }}
      />

      <ConfigurationField
        label="Coaching focus"
        labelTooltip="Any guidance that should shape how coaching goals are prioritized each week."
      >
        <Textarea
          value={settings.coaching_focus}
          placeholder={`"Always include a goal for compliance criteria" or "Balance sales and compliance goals evenly."`}
          isDisabled={fieldsContext.isReadOnly}
          onChange={(coachingFocus) => {
            onChange({ ...settings, coaching_focus: coachingFocus });
          }}
          innerTextAreaWrapperClassName={promptGuidanceHeightClassName}
        />
      </ConfigurationField>

      {/* Coaching goals are delivered by email only, so there is no delivery
          channel picker here. */}
      <ConfigurationField label="Who should be notified?">
        <div className="flex flex-col gap-3">
          <Checkbox
            isChecked={settings.should_notify_teammates}
            label="Each team member"
            isDisabled={fieldsContext.isReadOnly}
            onChange={(nextIsChecked) => {
              onChange({
                ...settings,
                should_notify_teammates: nextIsChecked,
              });
            }}
          />
          <Checkbox
            isChecked={settings.should_notify_managers}
            label="Each manager"
            isDisabled={fieldsContext.isReadOnly}
            onChange={(nextIsChecked) => {
              onChange({
                ...settings,
                should_notify_managers: nextIsChecked,
              });
            }}
          />
        </div>
      </ConfigurationField>

      {settings.should_notify_teammates || settings.should_notify_managers ? (
        <ConfigurationField label="What do they see?">
          <p className="text-sm text-foreground">
            {[
              settings.should_notify_teammates
                ? "Team members see their own results."
                : null,
              settings.should_notify_managers
                ? "Managers see their team's results."
                : null,
            ]
              .filter(Boolean)
              .join(" ")}
          </p>
        </ConfigurationField>
      ) : null}
    </>
  );
};

const weekdayOptions: readonly SimpleSelectOption<ScheduleWeekday>[] =
  allScheduleWeekdays.map((weekday) => ({
    value: weekday,
    label: weekdayLabels[weekday],
  }));

/**
 * The frequencies score movement's form offers. The shared trigger schema
 * admits hourly and daily too; a week-over-week comparison has nothing to say
 * on either cadence, so this form doesn't offer them.
 */
const scoreMovementFrequencies = ["weekly", "monthly"] as const;

const scoreMovementFrequencyOptions: readonly SimpleSelectOption<ScheduleFrequency>[] =
  scoreMovementFrequencies.map((frequency) => ({
    value: frequency,
    label: scheduleFrequencyLabels[frequency],
  }));

/** Seeds the run day and time when the stored trigger doesn't carry one. */
const DEFAULT_SCORE_MOVEMENT_WEEKDAY: ScheduleWeekday = "monday";
const DEFAULT_SCORE_MOVEMENT_TIME_OF_DAY = "09:00";

// Score movement's form does not offer the shared list's "last day" kind.
const scoreMovementMonthlyOptions: readonly MonthlyScheduleOption[] =
  monthlyScheduleOptions.filter(
    ({ schedule }) => schedule.kind !== "last_day_of_month",
  );

/**
 * The single weekday score movement's "Run day" picker shows. Its trigger
 * stores a list — this form writes exactly one — so the first is the selection,
 * and a trigger of a frequency this form doesn't offer falls back to the seed.
 */
function scoreMovementWeekday(trigger: RecurringSchedule): ScheduleWeekday {
  return trigger.frequency === "weekly"
    ? (trigger.weekdays[0] ?? DEFAULT_SCORE_MOVEMENT_WEEKDAY)
    : DEFAULT_SCORE_MOVEMENT_WEEKDAY;
}

/**
 * The run time score movement's picker shows. Both frequencies its form offers
 * carry one; the union's hourly variant has no time and reads as the seed.
 */
function scoreMovementTimeOfDay(trigger: RecurringSchedule): string {
  return trigger.frequency === "hourly"
    ? DEFAULT_SCORE_MOVEMENT_TIME_OF_DAY
    : trigger.time_of_day;
}

/**
 * Switches a score-movement automation between its form's two trigger
 * frequencies, carrying over the configured run time and seeding a default run
 * day for the target frequency.
 */
function changeTriggerFrequency(
  settings: AutomationReportAgentSettings,
  frequency: ScheduleFrequency,
): AutomationReportAgentSettings {
  const { trigger } = settings;
  if (trigger.frequency === frequency) {
    return settings;
  }
  const timeOfDay = scoreMovementTimeOfDay(trigger);
  return {
    ...settings,
    trigger:
      frequency === "monthly"
        ? {
            frequency: "monthly",
            schedule: { kind: "first_day_of_month" },
            time_of_day: timeOfDay,
          }
        : {
            frequency: "weekly",
            weekdays: [DEFAULT_SCORE_MOVEMENT_WEEKDAY],
            time_of_day: timeOfDay,
          },
  };
}

interface PlaybookScoreMovementSettingsFieldsProps extends AutomationFieldsContext {
  settings: AutomationReportAgentSettings;
  onChange: (settings: AutomationReportAgentSettings) => void;
}

const PlaybookScoreMovementSettingsFields: FC<
  PlaybookScoreMovementSettingsFieldsProps
> = (props) => {
  const { settings, onChange, ...fieldsContext } = props;
  return (
    <>
      <PlaybooksConfigurationField
        {...fieldsContext}
        playbookIds={getAutomationPlaybookIds(settings)}
        onChange={(playbookIds) => {
          onChange(updateAutomationPlaybookIds(settings, playbookIds));
        }}
      />

      <div className="grid gap-3 sm:max-w-xl sm:grid-cols-3">
        <ScheduleSubfield label="Trigger">
          <SimpleSelect
            label="Trigger"
            isLabelHidden
            options={scoreMovementFrequencyOptions}
            value={settings.trigger.frequency}
            isDisabled={fieldsContext.isReadOnly}
            onChange={(frequency) => {
              onChange(changeTriggerFrequency(settings, frequency));
            }}
          />
        </ScheduleSubfield>
        <ScheduleSubfield label="Run day">
          {settings.trigger.frequency === "monthly" ? (
            <SimpleSelect
              label="Run day"
              isLabelHidden
              options={scoreMovementMonthlyOptions}
              value={monthlyScheduleToOptionValue(settings.trigger.schedule)}
              isDisabled={fieldsContext.isReadOnly}
              onChange={(optionValue) => {
                const option = scoreMovementMonthlyOptions.find(
                  ({ value }) => value === optionValue,
                );
                if (option) {
                  onChange({
                    ...settings,
                    trigger: {
                      frequency: "monthly",
                      schedule: option.schedule,
                      time_of_day: scoreMovementTimeOfDay(settings.trigger),
                    },
                  });
                }
              }}
            />
          ) : (
            <SimpleSelect
              label="Run day"
              isLabelHidden
              options={weekdayOptions}
              value={scoreMovementWeekday(settings.trigger)}
              isDisabled={fieldsContext.isReadOnly}
              onChange={(weekday) => {
                onChange({
                  ...settings,
                  trigger: {
                    frequency: "weekly",
                    weekdays: [scheduleWeekdaySchema.parse(weekday)],
                    time_of_day: scoreMovementTimeOfDay(settings.trigger),
                  },
                });
              }}
            />
          )}
        </ScheduleSubfield>
        <ScheduleSubfield label="Run time">
          <div className="flex items-center gap-2">
            <TimeSelect
              label="Run time"
              isLabelHidden
              value={scoreMovementTimeOfDay(settings.trigger)}
              isDisabled={fieldsContext.isReadOnly}
              onChange={(timeOfDay) => {
                const { trigger } = settings;
                onChange({
                  ...settings,
                  // Rebuilt rather than spread: the shared union's hourly
                  // variant carries no run time to spread onto.
                  trigger:
                    trigger.frequency === "monthly"
                      ? { ...trigger, time_of_day: timeOfDay }
                      : {
                          frequency: "weekly",
                          weekdays: [scoreMovementWeekday(trigger)],
                          time_of_day: timeOfDay,
                        },
                });
              }}
            />
            <span className="shrink-0 text-sm text-muted-foreground">
              {formatPlatformZoneAbbreviation(DateTime.now())}
            </span>
          </div>
        </ScheduleSubfield>
      </div>

      <ConfigurationField
        label="Additional context"
        labelTooltip="Any guidance on what movements matter most to your team."
      >
        <Textarea
          value={settings.instructions}
          isDisabled={fieldsContext.isReadOnly}
          onChange={(instructions) => {
            onChange({ ...settings, instructions });
          }}
          innerTextAreaWrapperClassName={promptGuidanceHeightClassName}
        />
      </ConfigurationField>

      <RecipientsFields
        teammateOptions={fieldsContext.teammateOptions}
        editorAndReviewerTeammateOptions={
          fieldsContext.editorAndReviewerTeammateOptions
        }
        areRecipientOptionsLoading={fieldsContext.areRecipientOptionsLoading}
        isReadOnly={fieldsContext.isReadOnly}
        settings={settings}
        onChange={onChange}
      />
    </>
  );
};

interface RecipientsFieldsProps {
  teammateOptions: readonly TeammateOption[];
  editorAndReviewerTeammateOptions: readonly TeammateOption[];
  areRecipientOptionsLoading: boolean;
  /** Renders the recipient and delivery controls disabled (read-only view). */
  isReadOnly?: boolean;
  settings: AutomationReportAgentSettings;
  onChange: (settings: AutomationReportAgentSettings) => void;
  allowChannelDestination?: boolean;
}

/**
 * Manager + "Also subscribe" recipients, shared by the templates whose
 * recipient setup is identical (playbook score movement and lead burning).
 */
const RecipientsFields: FC<RecipientsFieldsProps> = (props) => {
  const {
    teammateOptions,
    editorAndReviewerTeammateOptions,
    areRecipientOptionsLoading,
    isReadOnly,
    settings,
    onChange,
    allowChannelDestination,
  } = props;
  return (
    <NotificationDeliveryFields
      delivery={getAutomationDeliveryEditorValue(settings)}
      onChangeDelivery={(delivery) => {
        onChange(updateAutomationDeliveryEditorValue(settings, delivery));
      }}
      allowChannelDestination={allowChannelDestination}
      isReadOnly={isReadOnly}
    >
      <ConfigurationField
        label="Notify managers"
        description="Each manager receives their own team's results."
        descriptionPlacement="aboveContent"
      >
        <ManagerScopeField
          value={getAutomationManagerRecipientsEditorValue(
            settings.report_definition,
          )}
          teammateOptions={teammateOptions}
          isLoading={areRecipientOptionsLoading}
          isDisabled={isReadOnly}
          onChange={(managerRecipients) => {
            onChange(
              updateAutomationManagerRecipientsEditorValue(
                settings,
                managerRecipients,
              ),
            );
          }}
        />
      </ConfigurationField>

      <ConfigurationField
        label="Also subscribe"
        description="These people receive a copy of every manager's result above."
        descriptionPlacement="aboveContent"
      >
        <TeammatePicker
          placeholder="Add people..."
          ariaLabel="People to also subscribe"
          teammateOptions={editorAndReviewerTeammateOptions}
          isLoading={areRecipientOptionsLoading}
          isDisabled={isReadOnly}
          value={
            getAutomationSubscribersEditorValue(settings.report_definition)
              .teammate_ids
          }
          onChange={(teammateIds) => {
            onChange(
              updateAutomationSubscribersEditorValue(settings, {
                teammate_ids: [...teammateIds],
              }),
            );
          }}
        />
      </ConfigurationField>
    </NotificationDeliveryFields>
  );
};

/**
 * The frequencies lead burning offers. It exposes no run-time picker — a daily
 * check has always run at the fixed end-of-day hour — so switching to daily
 * restores {@link LEAD_BURNING_DAILY_TIME_OF_DAY} rather than a chosen time.
 */
const leadBurningFrequencies = ["daily", "hourly"] as const;

const checkIntervalOptions: readonly SimpleSelectOption<
  (typeof leadBurningFrequencies)[number]
>[] = leadBurningFrequencies.map((frequency) => ({
  value: frequency,
  label: scheduleFrequencyLabels[frequency],
}));

interface LeadBurningSettingsFieldsProps extends AutomationFieldsContext {
  settings: AutomationReportAgentSettings;
  onChange: (settings: AutomationReportAgentSettings) => void;
}

const LeadBurningSettingsFields: FC<LeadBurningSettingsFieldsProps> = (
  props,
) => {
  const { settings, onChange, ...fieldsContext } = props;
  return (
    <>
      <PlaybooksConfigurationField
        {...fieldsContext}
        playbookIds={getAutomationPlaybookIds(settings)}
        onChange={(playbookIds) => {
          onChange(updateAutomationPlaybookIds(settings, playbookIds));
        }}
      />

      <ConfigurationField label="Check frequency">
        <div className="w-full sm:max-w-xs [&_button]:w-full">
          <SimpleSelect
            label="Check frequency"
            isLabelHidden
            options={checkIntervalOptions}
            value={settings.trigger.frequency}
            isDisabled={fieldsContext.isReadOnly}
            onChange={(checkInterval) => {
              onChange({
                ...settings,
                trigger:
                  checkInterval === "hourly"
                    ? { frequency: "hourly" }
                    : {
                        frequency: "daily",
                        time_of_day: LEAD_BURNING_DAILY_TIME_OF_DAY,
                      },
              });
            }}
          />
        </div>
      </ConfigurationField>

      <ConfigurationField
        label="Lead burning definition"
        labelTooltip="Describe the engagement pattern that should count as burning leads."
      >
        <Textarea
          value={settings.instructions}
          placeholder="Flag any teammate where more than 1 in 4 live calls ends in under 2 minutes"
          isDisabled={fieldsContext.isReadOnly}
          onChange={(instructions) => {
            onChange({ ...settings, instructions });
          }}
          innerTextAreaWrapperClassName={promptGuidanceHeightClassName}
        />
      </ConfigurationField>

      <RecipientsFields
        teammateOptions={fieldsContext.teammateOptions}
        editorAndReviewerTeammateOptions={
          fieldsContext.editorAndReviewerTeammateOptions
        }
        areRecipientOptionsLoading={fieldsContext.areRecipientOptionsLoading}
        isReadOnly={fieldsContext.isReadOnly}
        settings={settings}
        onChange={onChange}
        // Lead burning alerts call out individual flagged reps, so a shared
        // channel isn't offered — each manager is messaged privately.
        allowChannelDestination={false}
      />
    </>
  );
};

interface CustomSettingsFieldsProps extends AutomationFieldsContext {
  settings: AutomationReportAgentSettings;
  onChange: (settings: AutomationReportAgentSettings) => void;
}

const DEFAULT_CUSTOM_TIME_OF_DAY = "09:00";

interface CustomTriggerFieldProps {
  trigger: RecurringSchedule;
  onChange: (trigger: RecurringSchedule) => void;
  /** Renders the schedule controls disabled (read-only view). */
  isReadOnly?: boolean;
}

/** The custom agent's "Check frequency" compound control. */
const CustomTriggerField: FC<CustomTriggerFieldProps> = ({
  trigger,
  onChange,
  isReadOnly,
}) => {
  return (
    <ConfigurationField
      label="Check frequency"
      description={
        trigger.frequency === "hourly"
          ? "Checks once an hour, only when there's new call activity."
          : undefined
      }
      descriptionPlacement="aboveContent"
    >
      <RecurringScheduleFields
        className="flex flex-col gap-3"
        controlClassName="w-full sm:max-w-xs [&_button]:w-full"
        frequencyLabel="Check frequency"
        value={trigger}
        onChange={onChange}
        isDisabled={isReadOnly}
        seedTimeOfDay={DEFAULT_CUSTOM_TIME_OF_DAY}
        timeOfDayTrailing={
          <span className="shrink-0 text-sm text-muted-foreground">
            {formatPlatformZoneAbbreviation(DateTime.now())}
          </span>
        }
      />
    </ConfigurationField>
  );
};

interface CuratedTemplateSettingsFieldsProps extends AutomationFieldsContext {
  settings: AutomationReportAgentSettings;
  focusLabel: string;
  focusTooltip: string;
  focusPlaceholder: string;
  recipientDescription: string;
  onChange: (settings: AutomationReportAgentSettings) => void;
}

/** Curated template editor over the shared report settings. */
const CuratedTemplateSettingsFields: FC<CuratedTemplateSettingsFieldsProps> = (
  props,
) => {
  const {
    settings,
    focusLabel,
    focusTooltip,
    focusPlaceholder,
    recipientDescription,
    onChange,
    ...fieldsContext
  } = props;
  const definition = getAutomationReportDefinition(settings);
  const recipients = getAutomationRecipientSelection(definition);

  return (
    <>
      <PlaybooksConfigurationField
        {...fieldsContext}
        playbookIds={getAutomationPlaybookIds(settings)}
        onChange={(playbookIds) => {
          onChange(updateAutomationPlaybookIds(settings, playbookIds));
        }}
      />

      <CustomTriggerField
        trigger={settings.trigger}
        isReadOnly={fieldsContext.isReadOnly}
        onChange={(trigger) => {
          onChange({ ...settings, trigger });
        }}
      />

      <ConfigurationField label={focusLabel} labelTooltip={focusTooltip}>
        <Textarea
          value={settings.instructions}
          placeholder={focusPlaceholder}
          isDisabled={fieldsContext.isReadOnly}
          onChange={(instructions) => {
            onChange({ ...settings, instructions });
          }}
          innerTextAreaWrapperClassName={promptGuidanceHeightClassName}
        />
      </ConfigurationField>

      <NotificationDeliveryFields
        delivery={getAutomationDeliveryEditorValue(settings)}
        onChangeDelivery={(delivery) => {
          onChange(updateAutomationDeliveryEditorValue(settings, delivery));
        }}
        isReadOnly={fieldsContext.isReadOnly}
      >
        <ConfigurationField
          label="Notify"
          description={recipientDescription}
          descriptionPlacement="aboveContent"
        >
          <div className="flex flex-col gap-3">
            <TeammatePicker
              placeholder="Add teammates..."
              ariaLabel="Teammates to notify"
              teammateOptions={fieldsContext.teammateOptions}
              isLoading={fieldsContext.areRecipientOptionsLoading}
              isDisabled={fieldsContext.isReadOnly}
              value={recipients.teammate_ids}
              onChange={(teammateIds) => {
                onChange(
                  updateAutomationRecipientSelection(settings, {
                    ...recipients,
                    teammate_ids: [...teammateIds],
                  }),
                );
              }}
            />
            <GroupPicker
              placeholder="Add groups..."
              ariaLabel="Groups to notify"
              groupOptions={fieldsContext.groupOptions}
              isLoading={fieldsContext.areRecipientOptionsLoading}
              isDisabled={fieldsContext.isReadOnly}
              value={recipients.group_ids}
              onChange={(groupIds) => {
                onChange(
                  updateAutomationRecipientSelection(settings, {
                    ...recipients,
                    group_ids: [...groupIds],
                  }),
                );
              }}
            />
          </div>
        </ConfigurationField>
      </NotificationDeliveryFields>
    </>
  );
};

export interface CustomerPraiseDigestSettingsFieldsProps extends AutomationFieldsContext {
  settings: AutomationReportAgentSettings;
  onChange: (settings: AutomationReportAgentSettings) => void;
}

/** Customer praise digest's curated editor over the shared report settings. */
export const CustomerPraiseDigestSettingsFields: FC<
  CustomerPraiseDigestSettingsFieldsProps
> = (props) => (
  <CuratedTemplateSettingsFields
    {...props}
    focusLabel="Praise focus (optional)"
    focusTooltip="Optional guidance on which types of customer praise the agent should prioritize."
    focusPlaceholder="For example, prioritize specific ways your product or service helped customers, comparisons to competitors, and unprompted praise for teammates."
    recipientDescription="Everyone selected receives the same customer praise digest."
  />
);

interface PerfectCallsSettingsFieldsProps extends AutomationFieldsContext {
  settings: AutomationReportAgentSettings;
  onChange: (settings: AutomationReportAgentSettings) => void;
}

/** Perfect calls' curated editor over the shared report settings. */
const PerfectCallsSettingsFields: FC<PerfectCallsSettingsFieldsProps> = (
  props,
) => (
  <CuratedTemplateSettingsFields
    {...props}
    focusLabel="Call focus (optional)"
    focusTooltip="Optional guidance on which types of perfect calls or teammate behaviors the agent should prioritize."
    focusPlaceholder="For example, prioritize objection handling, discovery, relationship building, or calls for a specific product."
    recipientDescription="Everyone selected receives the same perfect calls digest."
  />
);

interface CustomRecipientsFieldProps {
  teammateOptions: readonly TeammateOption[];
  groupOptions: readonly GroupOption[];
  managerTeammateIds: readonly TeammateId[];
  areRecipientOptionsLoading: boolean;
  /** Renders the recipient and delivery controls disabled (read-only view). */
  isReadOnly?: boolean;
  onChangeResultMode: (mode: AutomationResultMode) => void;
  resultMode: AutomationResultMode;
  instanceType: AutomationReportInstances["type"];
  resultSelectionMode: AutomationResultSelectionMode;
  onChangeResultSelectionMode: (
    selectionMode: AutomationResultSelectionMode,
  ) => void;
  people: AutomationPeopleSelection;
  onChangePeople: (people: AutomationPeopleSelection) => void;
  recipients: AutomationRecipientSelection;
  onChange: (recipients: AutomationRecipientSelection) => void;
  delivery: AutomationDelivery;
  onChangeDelivery: (delivery: AutomationDelivery) => void;
  isPrivateOnly: boolean;
  isRoutingReadOnly: boolean;
}

/**
 * One sentence describing what the current people selection produces, with a
 * clause per role present in it. Roles are resolved from the current org chart
 * for display; the planner re-resolves them on every run.
 */
function describePeopleSelection(
  people: AutomationPeopleSelection,
  managerTeammateIds: readonly TeammateId[],
): string | null {
  const { hasTeammates, hasManagers } = partitionAutomationPeopleSelection(
    people,
    managerTeammateIds,
  );
  if (hasTeammates && hasManagers) {
    return "Teammates get a result about their own calls, and managers get a summary of their team's results.";
  }
  if (hasTeammates) {
    return "Teammates get a result about their own calls.";
  }
  if (hasManagers) {
    return "Managers get a result about their team's calls.";
  }
  return null;
}

/**
 * The custom agent's "Result type", delivery, and who the run covers. The
 * configured result types ask different questions of the same selection: a
 * shared result treats it as a distribution list ("Notify"), own results treat
 * it as the people who each get their own result — one picker of teammates and
 * managers, with a description of what the mix produces. The API-authored
 * group fan-out keeps its own selection controls.
 *
 * The third type configures nothing: the prompt above describes the results and
 * every run derives them, so this renders the choice alone. The configuration
 * the other two types wrote is left untouched underneath, and switching back
 * restores it.
 */
const CustomRecipientsField: FC<CustomRecipientsFieldProps> = (props) => {
  const {
    teammateOptions,
    groupOptions,
    managerTeammateIds,
    areRecipientOptionsLoading,
    isReadOnly,
    onChangeResultMode,
    resultMode,
    instanceType,
    resultSelectionMode,
    onChangeResultSelectionMode,
    people,
    onChangePeople,
    recipients,
    onChange,
    delivery,
    onChangeDelivery,
    isPrivateOnly,
    isRoutingReadOnly,
  } = props;
  const canChangeResultMode =
    resultMode === "custom" || instanceType !== "groups";
  const peopleDescription = describePeopleSelection(people, managerTeammateIds);

  const resultTypeField = (
    <ConfigurationField label="Result type">
      <div className="max-w-xl">
        <SimpleRadioGroup
          value={resultMode}
          onChange={(value) => {
            onChangeResultMode(automationResultModeSchema.parse(value));
          }}
        >
          <div className="flex flex-col gap-1">
            <SimpleRadioOption
              value="shared"
              isDisabled={isReadOnly || !canChangeResultMode}
            >
              One shared result
            </SimpleRadioOption>
            <p className="ml-6 text-xs leading-5 text-muted-foreground">
              Everyone gets the same result. Best for team-wide digests, like a
              customer feedback digest.
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <SimpleRadioOption
              value="own"
              isDisabled={isReadOnly || !canChangeResultMode}
            >
              One result each
            </SimpleRadioOption>
            <p className="ml-6 text-xs leading-5 text-muted-foreground">
              Each teammate gets their own result. Each group or manager gets an
              aggregated result across their teammates&apos; calls. Best for
              coaching and performance insights.
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <SimpleRadioOption value="custom" isDisabled={isReadOnly}>
              Custom
            </SimpleRadioOption>
            <p className="ml-6 text-xs leading-5 text-muted-foreground">
              Describe the results and who receives them in the prompt above.
            </p>
          </div>
        </SimpleRadioGroup>
      </div>
    </ConfigurationField>
  );

  if (resultMode === "custom") {
    return resultTypeField;
  }

  return (
    <NotificationDeliveryFields
      delivery={delivery}
      onChangeDelivery={onChangeDelivery}
      isReadOnly={isReadOnly}
      isRoutingReadOnly={isRoutingReadOnly}
      resultTypeField={resultTypeField}
      isPrivateOnly={isPrivateOnly}
    >
      {instanceType !== "shared" ? (
        <ConfigurationField label="Result recipients">
          {instanceType === "groups" ? (
            // The group fan-out is API-authored; the form keeps its legacy
            // selection controls rather than folding groups into the people
            // picker.
            <div className="flex flex-col gap-2">
              <SimpleRadioGroup
                value={resultSelectionMode}
                onChange={(value) => {
                  onChangeResultSelectionMode(
                    automationResultSelectionModeSchema.parse(value),
                  );
                }}
              >
                <SimpleRadioOption value="all" isDisabled={isReadOnly}>
                  All groups
                </SimpleRadioOption>
                <SimpleRadioOption value="specific" isDisabled={isReadOnly}>
                  Specific groups
                </SimpleRadioOption>
              </SimpleRadioGroup>
              {resultSelectionMode === "specific" ? (
                <GroupPicker
                  placeholder="Select groups"
                  ariaLabel="Groups that each get their own result"
                  groupOptions={groupOptions}
                  isLoading={areRecipientOptionsLoading}
                  isDisabled={isReadOnly}
                  value={recipients.group_ids}
                  onChange={(groupIds) => {
                    onChange({ teammate_ids: [], group_ids: [...groupIds] });
                  }}
                />
              ) : null}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <PeoplePicker
                ariaLabel="People who each get their own result"
                teammateOptions={teammateOptions}
                managerTeammateIds={managerTeammateIds}
                value={people}
                onChange={onChangePeople}
                isLoading={areRecipientOptionsLoading}
                isDisabled={isReadOnly}
              />
              {peopleDescription !== null ? (
                <p className="text-xs leading-5 text-muted-foreground">
                  {peopleDescription}
                </p>
              ) : null}
            </div>
          )}
        </ConfigurationField>
      ) : (
        <ConfigurationField
          label="Notify"
          description="Teammates and groups who receive the report."
          descriptionPlacement="aboveContent"
        >
          <div className="flex flex-col gap-3">
            <TeammatePicker
              placeholder="Add teammates..."
              ariaLabel="Teammates to notify"
              teammateOptions={teammateOptions}
              isLoading={areRecipientOptionsLoading}
              isDisabled={isReadOnly}
              value={recipients.teammate_ids}
              onChange={(teammateIds) => {
                onChange({ ...recipients, teammate_ids: [...teammateIds] });
              }}
            />
            <GroupPicker
              placeholder="Add groups..."
              ariaLabel="Groups to notify"
              groupOptions={groupOptions}
              isLoading={areRecipientOptionsLoading}
              isDisabled={isReadOnly}
              value={recipients.group_ids}
              onChange={(groupIds) => {
                onChange({ ...recipients, group_ids: [...groupIds] });
              }}
            />
          </div>
        </ConfigurationField>
      )}
    </NotificationDeliveryFields>
  );
};

const CustomSettingsFields: FC<CustomSettingsFieldsProps> = (props) => {
  const { settings, onChange, ...fieldsContext } = props;
  const definition = getAutomationReportDefinition(settings);
  const recipients = getAutomationRecipientSelection(definition);
  const resultMode = getAutomationResultMode(settings);
  const isCustomResultMode = resultMode === "custom";
  return (
    <>
      <PlaybooksConfigurationField
        {...fieldsContext}
        playbookIds={getAutomationPlaybookIds(settings)}
        onChange={(playbookIds) => {
          onChange(updateAutomationPlaybookIds(settings, playbookIds));
        }}
      />

      <CustomTriggerField
        trigger={settings.trigger}
        isReadOnly={fieldsContext.isReadOnly}
        onChange={(trigger) => {
          onChange({ ...settings, trigger });
        }}
      />

      <ConfigurationField
        label="What should this agent look for?"
        labelTooltip={
          isCustomResultMode
            ? "Describe the pattern, risk, opportunity, or theme to watch for, what each result should say, and who should receive it."
            : "Describe the pattern, risk, opportunity, or theme to watch for. Include what the summary should return, like teammate, reason, and call quote."
        }
      >
        <Textarea
          value={settings.instructions}
          placeholder={`What to look for:\nThe pattern, risk, opportunity, or theme this agent should watch for on calls — for example a specific objection, a compliance step being missed, a competitor mention, or a great call worth celebrating.\n\nWhat I want to receive:\nWhat each summary should include, such as the teammate, a short explanation of what happened, and a quote from the call.`}
          isDisabled={fieldsContext.isReadOnly}
          onChange={(instructions) => {
            onChange({ ...settings, instructions });
          }}
          innerTextAreaWrapperClassName={promptGuidanceHeightClassName}
        />
        {isCustomResultMode ? (
          <p className="mt-2 text-xs leading-5 text-muted-foreground">
            Say who each result is about and who should receive it — for example
            &ldquo;send each rep their own summary and copy their
            manager&rdquo;, or &ldquo;post one weekly digest in #revenue&rdquo;.
            Run a preview to see how it was read.
          </p>
        ) : null}
      </ConfigurationField>

      <CustomRecipientsField
        teammateOptions={fieldsContext.teammateOptions}
        groupOptions={fieldsContext.groupOptions}
        managerTeammateIds={fieldsContext.managerTeammateIds}
        areRecipientOptionsLoading={fieldsContext.areRecipientOptionsLoading}
        isReadOnly={fieldsContext.isReadOnly}
        onChangeResultMode={(mode) => {
          onChange(updateAutomationResultMode(settings, mode));
        }}
        resultMode={resultMode}
        instanceType={definition.instances.type}
        resultSelectionMode={getAutomationResultSelectionMode(definition)}
        onChangeResultSelectionMode={(selectionMode) => {
          onChange(
            updateAutomationResultSelectionMode(settings, selectionMode),
          );
        }}
        people={getAutomationPeopleSelection(definition)}
        onChangePeople={(people) => {
          onChange(
            updateAutomationPeopleSelection(
              settings,
              people,
              fieldsContext.managerTeammateIds,
            ),
          );
        }}
        recipients={recipients}
        onChange={(recipients) => {
          onChange(updateAutomationRecipientSelection(settings, recipients));
        }}
        delivery={getAutomationDeliveryEditorValue(settings)}
        onChangeDelivery={(delivery) => {
          onChange(updateAutomationDeliveryEditorValue(settings, delivery));
        }}
        isPrivateOnly={definition.routing.type === "instance_recipient"}
        isRoutingReadOnly={
          definition.instances.type === "groups" ||
          definition.synthesis?.group_by === "group"
        }
      />
    </>
  );
};

interface PlaybookConfigurationFieldProps extends AutomationFieldsContext {
  /**
   * Undefined only when the settings' playbook scope is empty — a state the
   * schema forbids but the array type cannot rule out; the select then shows
   * its placeholder.
   */
  playbookId: PlaybookId | undefined;
  onChange: (playbookId: PlaybookId) => void;
  /** Playbooks to disable in the dropdown (already taken by another agent). */
  disabledPlaybookIds?: readonly PlaybookId[];
}

const PlaybookConfigurationField: FC<PlaybookConfigurationFieldProps> = (
  props,
) => {
  const {
    playbookId,
    playbookOptions,
    arePlaybooksLoading,
    disabledPlaybookIds,
    isReadOnly,
    onChange,
  } = props;
  return (
    <ConfigurationField
      label="Playbook"
      description="The playbook whose calls this agent analyzes."
    >
      <div className="w-full sm:max-w-md [&_button]:w-full">
        <SimpleSelect
          label="Playbook"
          isLabelHidden
          options={playbookOptions}
          value={playbookId ?? null}
          disabledValues={disabledPlaybookIds}
          onChange={onChange}
          placeholder={
            arePlaybooksLoading ? "Loading playbooks" : "Select playbook"
          }
          isDisabled={
            arePlaybooksLoading || playbookOptions.length === 0 || isReadOnly
          }
        />
      </div>
    </ConfigurationField>
  );
};

interface PlaybooksConfigurationFieldProps extends Pick<
  AutomationFieldsContext,
  "playbookOptions" | "voiceAgentOptions" | "arePlaybooksLoading" | "isReadOnly"
> {
  playbookIds: readonly PlaybookId[];
  onChange: (playbookIds: PlaybookId[]) => void;
}

/**
 * Call scope for the report templates: a multi-select over the company's
 * playbooks and voice agents. A voice agent selection stores the playbook
 * backing the agent, so the saved scope is playbook ids either way.
 */
const PlaybooksConfigurationField: FC<PlaybooksConfigurationFieldProps> = (
  props,
) => {
  const {
    playbookIds,
    playbookOptions,
    voiceAgentOptions = [],
    arePlaybooksLoading,
    isReadOnly,
    onChange,
  } = props;

  const sections = buildPlaybookAndVoiceAgentSections(
    playbookOptions,
    voiceAgentOptions,
  );
  const allOptions = sections.flatMap((section) => section.options);

  const selectedNames = allOptions
    .filter((option) => playbookIds.includes(option.value))
    .map((option) => option.label);
  const selectionLabel =
    selectedNames.length === 1
      ? selectedNames[0]
      : selectedNames.length > 1
        ? `${selectedNames.length} selected`
        : undefined;

  return (
    <ConfigurationField
      label="Which calls should it analyze?"
      description="Pick more than one to look across them in one result"
    >
      <div className="w-full sm:max-w-md [&_button]:w-full">
        <MultiSelect
          label={selectionLabel}
          placeholder={
            arePlaybooksLoading ? "Loading options" : "Select options"
          }
          isDisabled={
            arePlaybooksLoading || allOptions.length === 0 || isReadOnly
          }
          sections={sections.map((section) => ({
            id: section.id,
            label: section.label,
            options: section.options.map((option) => ({
              id: option.value,
              label: option.label,
            })),
          }))}
          value={playbookIds}
          onChange={(nextIds) => {
            const selected = new Set(nextIds);
            const nextPlaybookIds = allOptions
              .filter((option) => selected.has(option.value))
              .map((option) => option.value);
            // Keep at least one playbook — the settings schema requires it.
            if (nextPlaybookIds.length > 0) {
              onChange(nextPlaybookIds);
            }
          }}
        />
      </div>
    </ConfigurationField>
  );
};

interface ScheduleSubfieldProps {
  label: string;
  children: ReactNode;
}

const ScheduleSubfield: FC<ScheduleSubfieldProps> = (props) => {
  const { label, children } = props;
  return (
    <div className="flex flex-col gap-1.5 [&_button]:w-full">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      {children}
    </div>
  );
};
